import { useEffect, useState } from "react";

const LOADING_STEPS = [
  "Connecting to market feeds...",
  "Fetching indices & futures...",
  "Loading sector data...",
  "Pulling latest news...",
  "Analyzing sentiment...",
  "Almost ready...",
];

export function PulseSkeleton() {
  const [progress, setProgress] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    // Animate progress bar to ~90% over time
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 90) return 90;
        // Fast start, slow finish
        const increment = p < 40 ? 8 : p < 70 ? 4 : 1.5;
        return Math.min(p + increment, 90);
      });
    }, 200);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Cycle through status messages
    const interval = setInterval(() => {
      setStepIdx((i) => (i + 1) % LOADING_STEPS.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      {/* Logo/icon pulse */}
      <div className="relative mb-8">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 border border-primary-500/30 flex items-center justify-center">
          <svg
            className="w-8 h-8 sm:w-10 sm:h-10 text-primary-400 animate-pulse"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          </svg>
        </div>
        {/* Glow ring */}
        <div className="absolute inset-0 rounded-2xl bg-primary-500/10 animate-ping" style={{ animationDuration: "2s" }} />
      </div>

      {/* Title */}
      <h2 className="text-lg sm:text-xl font-semibold text-surface-200 mb-2">
        Loading Market Pulse
      </h2>

      {/* Status message */}
      <p className="text-sm text-surface-400 mb-6 h-5 transition-opacity duration-300">
        {LOADING_STEPS[stepIdx]}
      </p>

      {/* Progress bar */}
      <div className="w-full max-w-xs sm:max-w-sm">
        <div className="h-1.5 rounded-full bg-surface-800/60 border border-surface-700/30 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-[11px] text-surface-500 mt-2 text-center">
          {Math.round(progress)}%
        </p>
      </div>

      {/* Skeleton preview below */}
      <div className="w-full mt-10 opacity-30 animate-pulse space-y-3 max-w-4xl">
        <div className="grid grid-cols-3 gap-3">
          <div className="h-16 sm:h-20 rounded-xl bg-surface-800/40" />
          <div className="h-16 sm:h-20 rounded-xl bg-surface-800/40" />
          <div className="h-16 sm:h-20 rounded-xl bg-surface-800/40" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="h-12 sm:h-16 rounded-xl bg-surface-800/40" />
          <div className="h-12 sm:h-16 rounded-xl bg-surface-800/40" />
        </div>
      </div>
    </div>
  );
}
